import { useState } from "react";
import { Modal } from "./ui";
import { Icon } from "./Icons";
import { actions, STUDIO } from "../lib/store";
import { fmtDate, relative } from "../lib/format";
import { openWhatsApp } from "../lib/whatsapp";
import { storeFile } from "../lib/sync";
import type { Project, TenderInvite, TenderDoc, TenderScopeSection } from "../lib/types";

const uid2 = (p = "") => p + Math.random().toString(36).slice(2, 9);

/* Inviting firms to price the work.
 *
 * The client usually has contractors they want to see bid. Rather than emailing
 * a folder around, they open a tender room: one link that any firm can register
 * on, which hands that firm its own access code and the documents, and leaves a
 * record of who asked. Others can be added simply to watch it happen. */

export function TenderRoom({ p, who, role }: { p: Project; who: string; role: "client" | "studio" }) {
  const [opening, setOpening] = useState(false);
  const tenders = p.tenders ?? [];
  const followers = p.followers ?? [];
  const drawn = (p.deliverables ?? []).filter((d: any) => !d.archived && (d.fileUrl || d.previewUrl)).length;

  return (
    <div className="col" style={{ gap: 18 }}>
      <div className="row between wrap" style={{ gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <h2 className="serif" style={{ fontSize: 30 }}>Tendering</h2>
          <p className="muted" style={{ marginTop: 6, maxWidth: 640, lineHeight: 1.7 }}>
            Invite contractors to price the work — the whole project or one package. Each firm registers on the link,
            gets its own access code and the documents, and {STUDIO.name} sees exactly who came forward.
          </p>
        </div>
        <button className="btn accent" onClick={() => setOpening(true)}><Icon.plus width={15} />Invite firms to tender</button>
      </div>

      {tenders.length === 0 ? (
        <div className="card pad">
          <div className="kicker">No tender open</div>
          <p className="muted" style={{ fontSize: 14, marginTop: 8, lineHeight: 1.65, maxWidth: 620 }}>
            When you're ready to get prices, open a tender and share the link with the firms you'd like to bid. There are
            currently <b>{drawn}</b> drawing{drawn === 1 ? "" : "s"} on file that would go with it.
          </p>
        </div>
      ) : tenders.map((t) => <TenderCard key={t.id} p={p} t={t} who={who} role={role} />)}

      {/* people watching */}
      <div className="card" style={{ overflow: "hidden" }}>
        <div className="row between wrap" style={{ padding: "13px 18px", borderBottom: followers.length ? "1px solid var(--line)" : "none", gap: 10 }}>
          <span className="row" style={{ gap: 8 }}><Icon.chat width={15} style={{ color: "var(--accent-ink)" }} /><span className="kicker">Following the process</span></span>
          <AddFollower p={p} who={who} />
        </div>
        {followers.length === 0 ? (
          <div className="dim" style={{ padding: "14px 18px", fontSize: 13, lineHeight: 1.6 }}>
            Add anyone who should be kept in the picture — a quantity surveyor, a funder, a family member. They're told
            when firms register and when the tender closes, and are never asked to do anything.
          </div>
        ) : followers.map((f, i) => (
          <div key={f.id} className="row between wrap" style={{ padding: "10px 18px", borderTop: i ? "1px solid var(--line)" : "none", gap: 10 }}>
            <span style={{ minWidth: 0 }}>
              <span style={{ fontSize: 13.5, fontWeight: 500 }}>{f.name}</span>
              <span className="dim" style={{ display: "block", fontSize: 12, marginTop: 1 }}>{f.email}{f.note ? ` · ${f.note}` : ""}</span>
            </span>
            <span className="row" style={{ gap: 8 }}>
              <span className="dim" style={{ fontSize: 11.5 }}>added {relative(f.addedOn)}</span>
              <button className="iconbtn" style={{ width: 26, height: 26 }} title="Remove" onClick={() => actions.removeFollower(p.id, f.id)}><Icon.close width={11} /></button>
            </span>
          </div>
        ))}
      </div>

      {opening && <OpenTenderModal p={p} who={who} role={role} onClose={() => setOpening(false)} />}
    </div>
  );
}

function TenderCard({ p, t, who, role }: { p: Project; t: TenderInvite; who: string; role: "client" | "studio" }) {
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const link = `https://${STUDIO.domain}/tender/${t.token}`;
  const live = t.registrations.filter((r) => r.status === "registered");
  const issued = t.scope === "selected" && t.docIds?.length ? t.docIds.length
    : t.scope === "package" ? undefined : liveSheets(p).length;
  const scopeCount = (t.scopeSections ?? []).reduce((n, s) => n + s.items.length, 0);

  return (
    <div className="card" style={{ overflow: "hidden", borderColor: t.closedOn ? "var(--line)" : "var(--accent)" }}>
      <div style={{ padding: "15px 18px" }}>
        <div className="row between wrap" style={{ gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div className="row wrap" style={{ gap: 8, alignItems: "baseline" }}>
              <span className="badge accent mono" style={{ fontSize: 10 }}>{t.ref}</span>
              <h3 className="serif" style={{ fontSize: 20 }}>{t.title}</h3>
              {t.closedOn ? <span className="badge" style={{ fontSize: 9 }}>Closed</span> : <span className="badge ok" style={{ fontSize: 9 }}><span className="dot" />Open</span>}
            </div>
            <div className="dim" style={{ fontSize: 12.5, marginTop: 4 }}>
              {t.scope === "whole" ? "The whole project" : t.scope === "selected" ? "A selection of the drawings" : "One package"}
              {issued != null ? ` · ${issued} drawing${issued === 1 ? "" : "s"} issued` : ""}
              {(t.extraDocs?.length ?? 0) > 0 ? ` · ${t.extraDocs!.length} tender document${t.extraDocs!.length === 1 ? "" : "s"}` : ""}
              {scopeCount ? ` · scope of works: ${scopeCount} items` : ""}
              {` · opened ${relative(t.createdOn)} by ${t.createdBy}`}
              {t.deadline ? ` · returns by ${fmtDate(t.deadline)}` : ""}
            </div>
          </div>
          <div className="row wrap" style={{ gap: 6 }}>
            <button className="btn ghost sm" onClick={() => { navigator.clipboard?.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1600); }}>
              <Icon.link width={13} />{copied ? "Copied" : "Copy link"}
            </button>
            <button className="btn ghost sm" onClick={() => openWhatsApp(`You're invited to tender for ${p.name}.\n\n${t.title}${t.deadline ? `\nReturns by ${fmtDate(t.deadline)}` : ""}\n\nRegister here for the documents:\n${link}`)}>
              <Icon.whatsapp width={13} />WhatsApp
            </button>
            <a className="btn ghost sm" href={`mailto:?subject=${encodeURIComponent(`Invitation to tender — ${p.name}`)}&body=${encodeURIComponent(`You're invited to tender for ${p.name}.\n\n${t.title}${t.deadline ? `\nReturns by ${fmtDate(t.deadline)}` : ""}\n\nRegister here for the documents:\n${link}`)}`}>
              <Icon.send width={13} />Email
            </a>
            <button className="btn ghost sm" onClick={() => setEditing(true)}><Icon.edit width={13} />Scope &amp; documents</button>
            <button className="btn ghost sm" onClick={() => actions.closeTender(p.id, t.id, who)}>{t.closedOn ? "Re-open" : "Close"}</button>
          </div>
        </div>
        {t.note && <p className="muted" style={{ fontSize: 13.5, marginTop: 10, lineHeight: 1.65 }}>{t.note}</p>}
        <div className="panel mono" style={{ padding: "8px 11px", marginTop: 12, fontSize: 11.5, overflow: "hidden", textOverflow: "ellipsis" }}>{link}</div>
      </div>
      {editing && <EditTenderModal p={p} t={t} who={who} role={role} onClose={() => setEditing(false)} />}

      <div className="row between" style={{ padding: "10px 18px", borderTop: "1px solid var(--line)", background: "var(--paper-2)" }}>
        <span className="kicker" style={{ fontSize: 9 }}>Registered</span>
        <span className="dim" style={{ fontSize: 12 }}>{live.length} firm{live.length === 1 ? "" : "s"}</span>
      </div>
      {t.registrations.length === 0 ? (
        <div className="dim" style={{ padding: "12px 18px", fontSize: 12.5 }}>Nobody has registered yet. Share the link above.</div>
      ) : t.registrations.map((r, i) => (
        <div key={r.id} className="row between wrap" style={{ padding: "10px 18px", borderTop: i ? "1px solid var(--line)" : "none", gap: 10, opacity: r.status === "registered" ? 1 : 0.55 }}>
          <span style={{ minWidth: 0 }}>
            <span style={{ fontSize: 13.5, fontWeight: 500 }}>{r.firm}</span>
            <span className="dim" style={{ display: "block", fontSize: 12, marginTop: 1 }}>{r.name} · {r.email}{r.phone ? ` · ${r.phone}` : ""}</span>
          </span>
          <span className="row wrap" style={{ gap: 8 }}>
            <span className="mono dim" style={{ fontSize: 11 }}>{r.code}</span>
            <span className="dim" style={{ fontSize: 11.5 }}>{relative(r.registeredOn)}</span>
            {role === "studio" && r.status === "registered" && (
              <button className="chip" style={{ fontSize: 10.5 }} onClick={() => actions.setTendererStatus(p.id, t.id, r.id, "declined")}>Rule out</button>
            )}
            {r.status !== "registered" && <span className="badge" style={{ fontSize: 8.5 }}>{r.status}</span>}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ---- what goes with the tender: sheet selection, uploads, scope of works -- */

function liveSheets(p: Project) {
  return (p.deliverables ?? []).filter((d: any) => !d.archived && (d.fileUrl || d.previewUrl));
}

/** Pick exactly which sheets are issued — grouped by discipline, whole
 *  disciplines toggleable in one click. */
function SheetPicker({ p, docIds, setDocIds }: { p: Project; docIds: string[]; setDocIds: (ids: string[]) => void }) {
  const sheets = liveSheets(p);
  const byDisc = sheets.reduce<Record<string, typeof sheets>>((acc, d: any) => { (acc[d.discipline || "Drawings"] ||= []).push(d); return acc; }, {});
  const has = (id: string) => docIds.includes(id);
  return (
    <div className="col" style={{ gap: 10, border: "1px solid var(--line)", borderRadius: 12, padding: 12, maxHeight: 260, overflowY: "auto" }}>
      {Object.entries(byDisc).map(([disc, list]) => {
        const allOn = list.every((d: any) => has(d.id));
        return (
          <div key={disc}>
            <div className="row between" style={{ marginBottom: 6 }}>
              <span className="kicker" style={{ fontSize: 9 }}>{disc} · {list.filter((d: any) => has(d.id)).length}/{list.length}</span>
              <button className="chip" style={{ fontSize: 10 }}
                onClick={() => setDocIds(allOn ? docIds.filter((id) => !list.some((d: any) => d.id === id)) : [...new Set([...docIds, ...list.map((d: any) => d.id)])])}>
                {allOn ? "None" : "All"}
              </button>
            </div>
            <div className="row wrap" style={{ gap: 5 }}>
              {list.map((d: any) => (
                <button key={d.id} className="chip mono" title={d.title}
                  style={{ fontSize: 10.5, background: has(d.id) ? "var(--accent)" : "var(--card)", color: has(d.id) ? "#fff" : "var(--ink-2)", borderColor: has(d.id) ? "var(--accent)" : "var(--line-2)" }}
                  onClick={() => setDocIds(has(d.id) ? docIds.filter((x) => x !== d.id) : [...docIds, d.id])}>
                  {d.code}
                </button>
              ))}
            </div>
          </div>
        );
      })}
      {sheets.length === 0 && <span className="dim" style={{ fontSize: 12.5 }}>Nothing drawn yet.</span>}
    </div>
  );
}

/** Documents uploaded onto the tender itself — a BOQ, preambles, the form of
 *  tender — issued alongside the drawings. */
function TenderDocsEditor({ p, role, who, docs, setDocs }: { p: Project; role: "client" | "studio"; who: string; docs: TenderDoc[]; setDocs: (d: TenderDoc[]) => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  return (
    <div className="col" style={{ gap: 8 }}>
      <div className="row wrap" style={{ gap: 8, alignItems: "center" }}>
        <label className="btn soft sm" style={{ cursor: "pointer" }}>
          <Icon.plus width={13} />{busy ? "Uploading…" : "Upload a document"}
          <input type="file" multiple style={{ display: "none" }} onChange={async (e) => {
            const files = Array.from(e.target.files ?? []).slice(0, 10 - docs.length);
            if (!files.length) return;
            setBusy(true); setErr("");
            const up: TenderDoc[] = [];
            for (const f of files) {
              try {
                const r = await storeFile(f, role === "client" ? p.client.code : undefined);
                up.push({ id: uid2("tdc-"), name: r.name, url: r.url, addedOn: new Date().toISOString(), addedBy: who });
              } catch { setErr(`${f.name} didn't upload — check the connection and try again.`); }
            }
            setDocs([...docs, ...up]);
            setBusy(false);
          }} />
        </label>
        <span className="dim" style={{ fontSize: 11.5 }}>The BOQ, preambles, the form of tender — anything the drawings alone don't carry.</span>
      </div>
      {docs.map((d) => (
        <div key={d.id} className="row between panel" style={{ padding: "7px 11px", gap: 8, fontSize: 12.5 }}>
          <a href={d.url} target="_blank" rel="noreferrer" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</a>
          <button className="iconbtn" style={{ width: 24, height: 24, flexShrink: 0 }} onClick={() => setDocs(docs.filter((x) => x.id !== d.id))}><Icon.close width={11} /></button>
        </div>
      ))}
      {err && <span style={{ color: "var(--danger)", fontSize: 12 }}>{err}</span>}
    </div>
  );
}

/** The scope of works, itemised — trade sections with priceable lines, drafted
 *  by hand or by the assistant from the note, the sheets and any pasted text. */
function ScopeBuilder({ p, role, title, note, docIds, sections, setSections }: {
  p: Project; role: "client" | "studio"; title: string; note: string; docIds: string[];
  sections: TenderScopeSection[]; setSections: (s: TenderScopeSection[]) => void;
}) {
  const [aiText, setAiText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const patchSection = (id: string, patch: Partial<TenderScopeSection>) =>
    setSections(sections.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  async function draft() {
    setBusy(true); setErr("");
    try {
      const sheets = liveSheets(p).filter((d: any) => !docIds.length || docIds.includes(d.id));
      const r = await fetch("/api/tender-scope", {
        method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title, note, text: aiText,
          drawings: sheets.map((d: any) => ({ code: d.code, title: d.title, discipline: d.discipline })),
          portalCode: role === "client" ? p.client.code : undefined,
        }),
      });
      const out = await r.json() as { sections?: { title: string; note?: string | null; items: { ref: string; description: string; qty?: number | null; unit?: string | null; note?: string | null }[] }[]; error?: string };
      if (!r.ok) { setErr(out.error ?? "The assistant couldn't draft that — try again."); return; }
      setSections([
        ...sections,
        ...(out.sections ?? []).map((s) => ({
          id: uid2("tss-"), title: s.title, note: s.note ?? undefined,
          items: s.items.map((i) => ({ id: uid2("tsi-"), ref: i.ref, description: i.description, qty: i.qty ?? undefined, unit: i.unit ?? undefined, note: i.note ?? undefined })),
        })),
      ]);
      setAiText("");
    } catch { setErr("Couldn't reach the assistant."); }
    finally { setBusy(false); }
  }

  return (
    <div className="col" style={{ gap: 10 }}>
      <div className="panel col" style={{ padding: 12, gap: 8 }}>
        <div className="row" style={{ gap: 8 }}><Icon.spark width={14} style={{ color: "var(--accent-ink)" }} /><span className="kicker" style={{ fontSize: 9 }}>Draft the scope with the studio mind</span></div>
        <textarea className="input" rows={3} value={aiText} onChange={(e) => setAiText(e.target.value)}
          placeholder="Paste a specification section, a BOQ extract or a scope letter — or leave this empty and it drafts from the tender note and the sheets being issued. Every line traces to what you give it." />
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button className="btn accent sm" disabled={busy} onClick={() => void draft()}>
            <Icon.spark width={13} />{busy ? "Drafting…" : "Draft the scope of works"}
          </button>
        </div>
        {err && <span style={{ color: "var(--danger)", fontSize: 12 }}>{err}</span>}
      </div>

      {sections.map((s, si) => (
        <div key={s.id} className="panel col" style={{ padding: 12, gap: 8 }}>
          <div className="row" style={{ gap: 8, alignItems: "center" }}>
            <span className="serif dim" style={{ fontSize: 16, width: 26 }}>{String(si + 1).padStart(2, "0")}</span>
            <input className="input" style={{ flex: 1, fontWeight: 600 }} value={s.title} onChange={(e) => patchSection(s.id, { title: e.target.value })} placeholder="Section — e.g. Substructure" />
            <button className="iconbtn" style={{ width: 26, height: 26 }} onClick={() => setSections(sections.filter((x) => x.id !== s.id))}><Icon.close width={12} /></button>
          </div>
          <input className="input" style={{ fontSize: 12.5 }} value={s.note ?? ""} onChange={(e) => patchSection(s.id, { note: e.target.value || undefined })} placeholder="What this section covers, in one line." />
          {s.items.map((it) => (
            <div key={it.id} className="row wrap" style={{ gap: 6 }}>
              <input className="input mono" style={{ width: 58, fontSize: 11.5, padding: "6px 7px" }} value={it.ref} onChange={(e) => patchSection(s.id, { items: s.items.map((x) => x.id === it.id ? { ...x, ref: e.target.value } : x) })} />
              <input className="input" style={{ flex: 2, minWidth: 180, fontSize: 12.5, padding: "6px 8px" }} value={it.description} onChange={(e) => patchSection(s.id, { items: s.items.map((x) => x.id === it.id ? { ...x, description: e.target.value } : x) })} placeholder="What is built — priceable on its own" />
              <input className="input mono" style={{ width: 64, fontSize: 11.5, padding: "6px 7px" }} value={it.qty ?? ""} onChange={(e) => patchSection(s.id, { items: s.items.map((x) => x.id === it.id ? { ...x, qty: e.target.value === "" ? undefined : +e.target.value } : x) })} placeholder="qty" />
              <input className="input" style={{ width: 56, fontSize: 11.5, padding: "6px 7px" }} value={it.unit ?? ""} onChange={(e) => patchSection(s.id, { items: s.items.map((x) => x.id === it.id ? { ...x, unit: e.target.value || undefined } : x) })} placeholder="unit" />
              <button className="iconbtn" style={{ width: 26, height: 26 }} onClick={() => patchSection(s.id, { items: s.items.filter((x) => x.id !== it.id) })}><Icon.close width={11} /></button>
            </div>
          ))}
          <div><button className="chip" style={{ fontSize: 11 }} onClick={() => patchSection(s.id, { items: [...s.items, { id: uid2("tsi-"), ref: `${si + 1}.${String(s.items.length + 1).padStart(2, "0")}`, description: "" }] })}><Icon.plus width={10} /> Add item</button></div>
        </div>
      ))}
      <div>
        <button className="btn ghost sm" onClick={() => setSections([...sections, { id: uid2("tss-"), title: "", items: [] }])}><Icon.plus width={13} />Add a section</button>
      </div>
    </div>
  );
}

/* The three-part works definition, shared by opening a tender and editing a
   live one: which sheets are issued, what extra documents go with them, and
   the itemised scope of works the firms price against. */
function WorksEditor({ p, role, who, title, note, scope, docIds, setDocIds, docs, setDocs, sections, setSections }: {
  p: Project; role: "client" | "studio"; who: string; title: string; note: string;
  scope: "whole" | "package" | "selected";
  docIds: string[]; setDocIds: (v: string[]) => void;
  docs: TenderDoc[]; setDocs: (v: TenderDoc[]) => void;
  sections: TenderScopeSection[]; setSections: (v: TenderScopeSection[]) => void;
}) {
  return (
    <>
      {scope === "selected" && (
        <div className="field"><label>Which drawings go with it <span className="dim">— only these are issued</span></label>
          <SheetPicker p={p} docIds={docIds} setDocIds={setDocIds} />
        </div>
      )}
      <div className="field"><label>Tender documents <span className="dim">— issued alongside the drawings</span></label>
        <TenderDocsEditor p={p} role={role} who={who} docs={docs} setDocs={setDocs} />
      </div>
      <div className="field"><label>Scope of works <span className="dim">— itemised, what the firms price line by line</span></label>
        <ScopeBuilder p={p} role={role} title={title} note={note} docIds={scope === "selected" ? docIds : []} sections={sections} setSections={setSections} />
      </div>
    </>
  );
}

function OpenTenderModal({ p, who, role, onClose }: { p: Project; who: string; role: "client" | "studio"; onClose: () => void }) {
  const packages = (p.packages ?? []).filter((x: any) => x.audience === "client" || role === "studio");
  const [scope, setScope] = useState<"whole" | "package" | "selected">("whole");
  const [packageId, setPackageId] = useState<string>(packages[0]?.id ?? "");
  const [docIds, setDocIds] = useState<string[]>([]);
  const [docs, setDocs] = useState<TenderDoc[]>([]);
  const [sections, setSections] = useState<TenderScopeSection[]>([]);
  const [title, setTitle] = useState(`${p.name} — invitation to tender`);
  const [note, setNote] = useState("");
  const [deadline, setDeadline] = useState("");
  const drawn = liveSheets(p).length;

  return (
    <Modal open onClose={onClose} title="Invite firms to tender" wide>
      <div className="col" style={{ gap: 14 }}>
        <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.65 }}>
          This creates one link you can send to as many firms as you like. Each one registers itself, is given its own
          access code, and gets exactly the documents and scope you set out below.
        </p>
        <div className="field"><label>What are they pricing?</label>
          <div className="row wrap" style={{ gap: 6 }}>
            {([["whole", `The whole project · ${drawn} drawings`], ["selected", "A selection of the drawings"], ["package", "One package"]] as const).map(([k, label]) => (
              <button key={k} className="chip" disabled={k === "package" && !packages.length} onClick={() => setScope(k)}
                style={{ fontSize: 12, padding: "6px 12px", background: scope === k ? "var(--accent)" : "var(--card)", color: scope === k ? "#fff" : "var(--ink-2)", borderColor: scope === k ? "var(--accent)" : "var(--line-2)", opacity: k === "package" && !packages.length ? 0.45 : 1 }}>
                {label}
              </button>
            ))}
          </div>
          {scope === "package" && packages.length > 0 && (
            <select className="input" style={{ marginTop: 8 }} value={packageId} onChange={(e) => setPackageId(e.target.value)}>
              {packages.map((x: any) => <option key={x.id} value={x.id}>{x.ref} — {x.title}</option>)}
            </select>
          )}
          {scope === "package" && !packages.length && (
            <div className="dim" style={{ fontSize: 12, marginTop: 6 }}>No packages have been prepared yet — ask {STUDIO.name} to put one together, or tender the whole project.</div>
          )}
        </div>
        <div className="field"><label>Title</label><input className="input" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
        <div className="field"><label>What you'd like them to price <span className="dim">(shown on the link)</span></label>
          <textarea className="input" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Main contract works including substructure, frame and envelope. Fit-out is excluded. Please price on the drawings issued and note any assumptions." />
        </div>

        <WorksEditor p={p} role={role} who={who} title={title} note={note} scope={scope}
          docIds={docIds} setDocIds={setDocIds} docs={docs} setDocs={setDocs} sections={sections} setSections={setSections} />

        <div className="field" style={{ maxWidth: 220 }}><label>Returns by <span className="dim">(optional)</span></label>
          <input className="input" type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
        </div>
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn accent" disabled={!title.trim() || (scope === "package" && !packageId) || (scope === "selected" && !docIds.length)}
            onClick={() => {
              actions.openTender(p.id, {
                title, scope, packageId: scope === "package" ? packageId : undefined,
                docIds: scope === "selected" ? docIds : undefined,
                extraDocs: docs, scopeSections: sections.filter((s) => s.title.trim()),
                note, deadline, by: who, byRole: role,
              });
              onClose();
            }}>
            <Icon.check width={15} />Open the tender
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* Adjust a LIVE tender — trim or grow the issued set, add the BOQ that arrived
   late, refine the scope. Registered firms see the change on their next visit. */
function EditTenderModal({ p, t, who, role, onClose }: { p: Project; t: TenderInvite; who: string; role: "client" | "studio"; onClose: () => void }) {
  const [docIds, setDocIds] = useState<string[]>(t.docIds ?? (t.scope === "selected" ? [] : liveSheets(p).map((d: any) => d.id)));
  const [docs, setDocs] = useState<TenderDoc[]>(t.extraDocs ?? []);
  const [sections, setSections] = useState<TenderScopeSection[]>(t.scopeSections ?? []);
  const scope = t.scope === "package" ? "package" : "selected";
  return (
    <Modal open onClose={onClose} title={`${t.ref} — scope & documents`} wide>
      <div className="col" style={{ gap: 14 }}>
        <p className="muted" style={{ fontSize: 13, lineHeight: 1.6 }}>
          Changes land on the tender link immediately — every registered firm sees the same set the next time they open it.
        </p>
        <WorksEditor p={p} role={role} who={who} title={t.title} note={t.note ?? ""} scope={scope}
          docIds={docIds} setDocIds={setDocIds} docs={docs} setDocs={setDocs} sections={sections} setSections={setSections} />
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn accent" disabled={scope === "selected" && !docIds.length}
            onClick={() => {
              actions.updateTenderDetails(p.id, t.id, {
                scope: t.scope === "package" ? "package" : "selected",
                docIds: t.scope === "package" ? t.docIds : docIds,
                extraDocs: docs, scopeSections: sections.filter((s) => s.title.trim()),
              }, who);
              onClose();
            }}>
            <Icon.check width={15} />Save
          </button>
        </div>
      </div>
    </Modal>
  );
}

function AddFollower({ p, who }: { p: Project; who: string }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", note: "" });
  const ok = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim());
  if (!open) return <button className="btn ghost sm" onClick={() => setOpen(true)}><Icon.plus width={13} />Add someone</button>;
  return (
    <Modal open onClose={() => setOpen(false)} title="Add someone to follow the process">
      <div className="col" style={{ gap: 13 }}>
        <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.65 }}>
          They'll be emailed when firms register and when the tender closes. They aren't given the documents and nothing
          is asked of them.
        </p>
        <div className="field"><label>Name</label><input className="input" autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div className="field"><label>Email</label><input className="input" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
        <div className="field"><label>Why they're watching <span className="dim">(optional)</span></label><input className="input" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Quantity surveyor" /></div>
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" onClick={() => setOpen(false)}>Cancel</button>
          <button className="btn accent" disabled={!ok} onClick={() => { actions.addFollower(p.id, { ...f, by: who }); setF({ name: "", email: "", note: "" }); setOpen(false); }}>
            <Icon.check width={15} />Add them
          </button>
        </div>
      </div>
    </Modal>
  );
}
